import { useDispatch } from 'react-redux';
import { useCallback, useState } from 'react';
import { trackEvent, deviceInfo, SL, TABLET_LARGE_WIDTH } from '@surfline/web-common';
import { postUser } from '../../../../api/user';
import { setupUser, triggerModalRecompute, createMeter } from '../../../../actions';
import config from '../../../../config';
import { useUserSelector } from '../../../../selectors/user';
import { useMeterSelector } from '../../../../selectors/metering';
import {
  CLICKED_CREATE_ACCOUNT,
  CLICKED_POPUP,
  CREATED_ACCOUNT,
  FORCED_REGISTRATION,
} from '../../../../common/constants';

const { clientId } = config;

export const useCreateAccount = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useUserSelector();
  const meter = useMeterSelector();

  const { anonymousId } = meter || {};
  const { location, countryCode } = user;
  const { time_zone: timezone } = location || {};

  const createAccount = useCallback(
    /**
     * @param {Object} values
     * @param {string} values.firstName
     * @param {string} values.lastName
     * @param {string} values.password
     * @param {string} values.email
     * @param {boolean?} values.staySignedIn
     * @param {boolean?} values.receivePromotions
     * @return {Promise<void>}
     */
    async (values) => {
      try {
        trackEvent(CLICKED_POPUP, {
          action: CLICKED_CREATE_ACCOUNT,
          category: FORCED_REGISTRATION,
        });

        setLoading(true);
        const { firstName, lastName, email, password, receivePromotions, staySignedIn } = values;
        const { deviceId, deviceType } = deviceInfo();

        const valuesForRegistration = {
          firstName,
          lastName,
          email,
          password,
          brand: SL,
          settings: {
            receivePromotions,
          },
          client_id: clientId,
          device_id: deviceId,
          device_type: deviceType,
        };

        const { userId, accessToken } = await postUser(!staySignedIn, valuesForRegistration);

        window?.analytics?.identify(userId);
        trackEvent(CREATED_ACCOUNT, {
          pageName: window?.location?.pathname,
          location: FORCED_REGISTRATION,
          platform: 'web',
          isMobileView: window?.innerWidth <= TABLET_LARGE_WIDTH,
        });

        await dispatch(setupUser({ accessToken }));
        await dispatch(
          createMeter({
            accessToken,
            anonymousId,
            countryCode,
            timezone,
            performMeterSurfCheck: true,
          }),
        );

        setLoading(false);
        dispatch(triggerModalRecompute());
      } catch (err) {
        setLoading(false);
        setError(err.message);
      }
    },
    [anonymousId, dispatch, countryCode, timezone],
  );
  return { createAccount, loading, error };
};
